// 2026-09-24 - FormuLearning - Step 7 Final Report 카드 (이슈 #59)
// 파일 위치: frontend/src/features/step7/Step7Card.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 리포트 재생성·전체 재생성·표지·결재 입력 비활성(Download .docx·절 펼치기는 열어 둔다).
//
// 제형연구보고서 확인·재생성·다운로드. 각 절은 귀속 Step 의 Save and continue 때 이미 반영돼 있고
// (MainPage), 여기서는 상태를 보고 빠진 곳을 «리포트 재생성»으로 채운다(설계 D-3).
// 다운로드는 언제든 가능 — Step 1~6 미완이면 «초안» 표기 docx(D-1).
// 1장 목적·8장 고찰은 재생성 때만 AI 로 쓴다(D-2).

import { useCallback, useEffect, useState } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { useBusyStore } from '../../stores/busyStore'
import { toast } from '../../stores/toastStore'
import { apiErrorMessage } from '../../lib/apiError'
import { downloadReport, getReport, regenerateReport, saveReportMeta } from '../../api/report'
import type { ReportMeta, ReportSection, ReportView, SectionStatus } from '../../api/report'
import ReportBlocks from './ReportBlocks'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

const STATUS: Record<SectionStatus, { label: string; color: string }> = {
  fresh: { label: '반영됨', color: 'var(--color-success)' },
  stale: { label: '갱신 필요', color: 'var(--color-warn)' },
  empty: { label: '미반영', color: 'var(--color-text-muted)' },
  static: { label: '실측 기입', color: 'var(--color-text-secondary)' },
}

const btn = 'rounded px-3 py-1.5 text-[11px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50'

export default function Step7Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  const gating = useProjectStore((s) => s.gating)
  const [view, setView] = useState<ReportView | null>(null)
  const [meta, setMeta] = useState<ReportMeta | null>(null)
  const [busy, setBusy] = useState<'regen' | 'download' | 'meta' | null>(null)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const canWrite = useCanWrite()

  // 다른 카드의 Save and continue 뒤 게이팅이 갱신되면 절 상태도 다시 읽는다.
  useEffect(() => {
    if (!projectId) return
    getReport(projectId).then((v) => { setView(v); setMeta(v.meta) }).catch(() => setView(null))
  }, [projectId, gating])

  useEffect(() => {
    if (busy !== 'regen') return
    const id = crypto.randomUUID()
    useBusyStore.getState().begin({ id, step: 7, label: 'Step 7 리포트 재생성' })
    return () => useBusyStore.getState().end(id)
  }, [busy])

  const regenerate = useCallback(async (mode: 'stale' | 'all') => {
    if (!projectId) return
    setBusy('regen')
    try {
      setView(await regenerateReport(projectId, mode))
      toast('리포트를 재생성했습니다.')
    } catch (e) {
      toast(apiErrorMessage(e), 'warn')
    } finally {
      setBusy(null)
    }
  }, [projectId])

  const download = useCallback(async () => {
    if (!projectId) return
    setBusy('download')
    try {
      await downloadReport(projectId)
      setView(await getReport(projectId))
    } catch (e) {
      toast(apiErrorMessage(e), 'warn')
    } finally {
      setBusy(null)
    }
  }, [projectId])

  const commitMeta = useCallback(async (next: ReportMeta) => {
    if (!projectId) return
    setMeta(next)
    try {
      await saveReportMeta(projectId, next)
    } catch (e) {
      toast(apiErrorMessage(e), 'warn')
    }
  }, [projectId])

  if (!projectId || !view || !meta) return <p className="text-xs text-text-secondary">Loading...</p>

  const pending = view.sections.filter((s) => s.status === 'stale' || s.status === 'empty').length
  const autoProduct = view.sections.find((s) => s.code === 'cover')?.blocks.find((b) => b.type === 'paragraph')
  const rows = view.sections.filter((s) => s.code !== 'cover')

  return (
    <div>
      {/* 상태 · 동작 */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="rounded px-2 py-1 text-[11px] font-semibold"
          style={view.is_final
            ? { background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
            : { background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>
          {view.is_final ? '최종본 작성 가능' : `초안 — 미완료 Step ${view.missing_steps.join(', ')}`}
        </span>
        <span className="text-[11px] text-text-secondary">갱신 필요·미반영 {pending}절</span>
        <div className="ml-auto flex gap-2">
          <button type="button" className={btn} disabled={busy !== null || !canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} onClick={() => regenerate('stale')}
            style={{ border: '1px solid var(--border-teal)', color: 'var(--color-accent-teal)' }}>
            {busy === 'regen' ? '재생성 중…' : '리포트 재생성'}
          </button>
          <button type="button" className={btn} disabled={busy !== null || !canWrite} onClick={() => regenerate('all')}
            style={{ border: '1px solid var(--border-subtle)', color: 'var(--color-text-secondary)' }}
            title={canWrite ? '모든 절을 다시 만든다' : READ_ONLY_TITLE}>
            전체 재생성
          </button>
          <button type="button" className={btn} disabled={busy !== null} onClick={download}
            style={{ backgroundColor: 'var(--btn-accent-teal)', color: 'var(--color-bg-base)' }}>
            {busy === 'download' ? '만드는 중…' : 'Download .docx'}
          </button>
        </div>
      </div>
      <p className="mb-4 text-[10px] text-text-secondary">
        각 Step 의 Save and continue 때 해당 절이 반영됩니다. «리포트 재생성»은 갱신 필요 절과 1장 목적·8장 고찰(AI)을 다시 씁니다.
        {view.last_download && ` 마지막 다운로드 v${view.last_download.version}.`}
      </p>

      {/* 표지 · 결재 */}
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">표지 · 결재</h4>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <MetaField key={`p-${meta.product}`} label="제품명" value={meta.product} placeholder={autoProduct && 'text' in autoProduct ? autoProduct.text : ''}
          onCommit={(v) => commitMeta({ ...meta, product: v })} />
        <MetaField key={`s-${meta.subtitle}`} label="부제" value={meta.subtitle} placeholder="예: - 일반 습식과립법 적용 -"
          onCommit={(v) => commitMeta({ ...meta, subtitle: v })} />
        <MetaField key={`d-${meta.report_date}`} label="작성일" value={meta.report_date} placeholder="비우면 다운로드 날짜"
          onCommit={(v) => commitMeta({ ...meta, report_date: v })} />
        <MetaField key={`o-${meta.organization}`} label="기관" value={meta.organization} placeholder="예: 국민대학교 산업약학연구실"
          onCommit={(v) => commitMeta({ ...meta, organization: v })} />
        {meta.approvals.map((a, i) => (
          <MetaField key={`${a.role}-${a.name}`} label={`${a.role} (${a.position})`} value={a.name} placeholder="성명"
            onCommit={(v) => commitMeta({ ...meta, approvals: meta.approvals.map((x, j) => (j === i ? { ...x, name: v } : x)) })} />
        ))}
      </div>

      {/* 절 목록 */}
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">보고서 구성</h4>
      <div className="flex flex-col">
        {rows.map((s, i) => {
          const head = i === 0 || rows[i - 1].chapter !== s.chapter ? s.chapter : ''
          return (
            <div key={s.code}>
              {head && <p className="mt-3 mb-1 text-xs font-semibold text-text-primary">{head}</p>}
              <SectionRow s={s} open={!!open[s.code]} onToggle={() => setOpen((o) => ({ ...o, [s.code]: !o[s.code] }))} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SectionRow({ s, open, onToggle }: { s: ReportSection; open: boolean; onToggle: () => void }) {
  const st = STATUS[s.status]
  return (
    <div className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-bg-input">
        <span className="w-3 text-[10px] text-text-secondary">{open ? '▾' : '▸'}</span>
        <span className="text-[11px] text-text-body">{[s.number, s.title].filter(Boolean).join(' ')}</span>
        <span className="text-[10px] text-text-secondary">{s.step && s.step <= 6 ? `Step ${s.step}` : s.kind === 'narrative' ? 'AI 서술' : ''}</span>
        <span className="ml-auto text-[10px] font-medium" style={{ color: st.color }}>{st.label}</span>
      </button>
      {open && (
        <div className="px-6 pb-3 pt-1">
          {s.blocks.length ? <ReportBlocks blocks={s.blocks} /> : <p className="text-[10px] text-text-secondary">아직 반영되지 않았습니다.</p>}
        </div>
      )}
    </div>
  )
}

function MetaField({ label, value, placeholder, onCommit }: {
  label: string; value: string; placeholder?: string; onCommit: (v: string) => void
}) {
  // 서버값이 바뀌면 부모가 key 로 다시 마운트한다 — 입력 중 값은 이 안에서만 든다.
  const [v, setV] = useState(value)
  const canWrite = useCanWrite()
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] text-text-secondary">{label}</span>
      <input value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
        disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE}
        onBlur={() => { if (v !== value) onCommit(v) }}
        className="rounded border px-2 py-1 text-[11px] text-text-body disabled:opacity-60"
        style={{ borderColor: 'var(--border-subtle)', background: 'var(--color-bg-input)' }} />
    </label>
  )
}
